import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

import { CalendarPopover } from "./CalendarPopover";
import { DateInput } from "./DateInput";
import { useCalendarPosition } from "./usePosition";
import type { DateRangePickerProps } from "./types";

/** Dual-month range picker with text inputs and a floating popover. */
export function DateRangePicker({
  value,
  onChange,
  disabled,
  className,
  id,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState<"start" | "end" | null>(null);

  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);
  const firstNavRef = useRef<HTMLButtonElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const calPos = useCalendarPosition(wrapRef, open, popoverRef);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inAnchor = wrapRef.current?.contains(target);
      const inPopover = popoverRef.current?.contains(target);
      if (!inAnchor && !inPopover) {
        setOpen(false);
        setFocused(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  return (
    <div ref={wrapRef} className={cn("relative inline-block", className)}>
      <div className="flex flex-wrap items-center gap-2.5">
        <DateInput
          id={id ? `${id}-start` : undefined}
          label="Start date"
          value={value.start}
          inputRef={startRef}
          active={focused === "start"}
          onCommit={(d) => {
            onChange({ start: d, end: value.end });
            if (d && !disabled) setOpen(true);
          }}
          onFocus={() => {
            if (disabled) return;
            setFocused("start");
            setOpen(true);
          }}
          onTab={(shift) => {
            if (!shift) {
              endRef.current?.focus();
              setFocused("end");
            }
          }}
        />
        <span className="text-lg font-light text-muted-foreground">–</span>
        <DateInput
          id={id ? `${id}-end` : undefined}
          label="End date"
          value={value.end}
          inputRef={endRef}
          active={focused === "end"}
          onCommit={(d) => onChange({ start: value.start, end: d })}
          onFocus={() => {
            if (disabled) return;
            setFocused("end");
            setOpen(true);
          }}
          onTab={(shift) => {
            if (shift) {
              startRef.current?.focus();
              setFocused("start");
            } else {
              firstNavRef.current?.focus();
            }
          }}
        />
      </div>

      {open && !disabled &&
        // Portal to body so the calendar escapes any sibling sticky
        // stacking context — same fix pattern as the DS Tooltip.
        createPortal(
          <div
            ref={popoverRef}
            // pointer-events-auto: a modal Radix Dialog sets `pointer-events:
            // none` on <body>, which this body-portaled popover would otherwise
            // inherit (making the calendar unclickable). Stopping pointerdown
            // keeps the Dialog's DismissableLayer from reading a day click as an
            // outside interaction and closing the whole dialog.
            className="fixed z-50 animate-scale-in pointer-events-auto"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              top: calPos.top ?? undefined,
              bottom: calPos.bottom ?? undefined,
              left: calPos.left ?? undefined,
              right: calPos.right ?? undefined,
            }}
          >
            <CalendarPopover
              rangeMode
              stacked={calPos.stacked}
              firstNavRef={firstNavRef}
              startDate={value.start}
              endDate={value.end}
              onConfirm={(s, e) => {
                onChange({ start: s, end: e });
                setOpen(false);
                setFocused(null);
              }}
              onCancel={() => {
                setOpen(false);
                setFocused(null);
              }}
              onClear={() => onChange({ start: null, end: null })}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
